import { RefObject, useEffect } from "react";

/**
 * After ReactGrid opens the context menu, adjust top/left so the panel stays in the viewport.
 */
export function useClampReactGridContextMenu(
  containerRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const clamp = () => {
      const menu = root.querySelector(".rg-context-menu") as HTMLElement | null;
      if (!menu) return;
      const rect = menu.getBoundingClientRect();
      const margin = 8;
      let top = rect.top;
      let left = rect.left;

      if (rect.bottom > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - margin - rect.height);
      }
      if (top < margin) top = margin;

      if (rect.right > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - rect.width);
      }
      if (left < margin) left = margin;

      menu.style.top = `${top}px`;
      menu.style.left = `${left}px`;
    };

    const scheduleClamp = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(clamp);
      });
    };

    const onContextMenu = () => scheduleClamp();

    root.addEventListener("contextmenu", onContextMenu, true);

    let raf = 0;
    const mo = new MutationObserver(() => {
      if (!root.querySelector(".rg-context-menu")) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        clamp();
      });
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      root.removeEventListener("contextmenu", onContextMenu, true);
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [containerRef]);
}
